import React, { useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import '../../marketing.css';

const NAV = [
  { key: 'fbr', to: '/fbr-digital-invoicing', label: 'FBR Invoicing' },
  { key: 'pra', to: '/pra-digital-invoicing', label: 'PRA Invoicing' },
  { key: 'tax', to: '/tax-filing', label: 'Tax Filing' },
  { key: 'odoo', to: '/odoo-integration', label: 'Odoo' },
];

const MarketingLayout = ({ active, children }) => {
  const navigate = useNavigate();
  const location = useLocation();

  // React Router does not scroll to a hash on its own, so a link like /#pricing from
  // another page just changed the URL and left you at the top. This handles both the
  // cross page case and clicking the same link twice.
  useEffect(() => {
    if (!location.hash) {
      window.scrollTo({ top: 0, behavior: 'auto' });
      return;
    }
    const id = location.hash.slice(1);
    // The target may not be painted yet on a fresh route, so retry briefly.
    let tries = 0;
    const tick = () => {
      const el = document.getElementById(id);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      } else if (tries++ < 20) {
        window.setTimeout(tick, 50);
      }
    };
    tick();
  }, [location.pathname, location.hash]);

  // Alternate the banded sections between the two approved treatments. Doing it here means
  // page markup stays unaware of its position in the page order.
  useEffect(() => {
    const alts = document.querySelectorAll('.marketing .section-alt');
    alts.forEach((el, i) => {
      el.classList.remove('alt-b', 'alt-d');
      el.classList.add(i % 2 === 0 ? 'alt-b' : 'alt-d');
    });
  }, [location.pathname]);

  // Reveal each band as it comes into view. Sections render visible; we only hide the
  // ones that start below the fold, so a failure here can never blank the page.
  useEffect(() => {
    const targets = Array.from(
      document.querySelectorAll('.marketing section, .marketing .stats')
    );
    if (!targets.length) return undefined;

    const reveal = (el) => el.classList.remove('will-reveal');
    const revealAll = () => targets.forEach(reveal);

    const reduced = window.matchMedia
      && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduced || !('IntersectionObserver' in window)) return undefined;

    const belowFold = targets.filter(
      (t) => t.getBoundingClientRect().top > window.innerHeight * 0.9
    );
    belowFold.forEach((t) => t.classList.add('will-reveal'));

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            reveal(entry.target);
            io.unobserve(entry.target);
          }
        });
      },
      { rootMargin: '0px 0px -6% 0px', threshold: 0.02 }
    );
    belowFold.forEach((t) => io.observe(t));

    // Backstop: whatever happens, nothing stays hidden for more than a couple of seconds.
    const safety = window.setTimeout(revealAll, 2500);

    return () => {
      io.disconnect();
      window.clearTimeout(safety);
      revealAll();
    };
  }, [location.pathname]);

  return (
    <div className="marketing">
      <nav className="nav">
        <div className="nav-inner">
          <Link className="brand" to="/">
            <img src="/marketing/images/axiom_square.jpeg" alt="AXIOM SQUARE Business Consultants" />
          </Link>
          <ul className="nav-links">
            {NAV.map((n) => (
              <li key={n.key}>
                <Link to={n.to} className={active === n.key ? 'active' : undefined}>{n.label}</Link>
              </li>
            ))}
            <li><Link to="/#pricing">Pricing</Link></li>
            <li><a href="/guides/">Blogs</a></li>
            <li><Link to="/#contact">Contact</Link></li>
          </ul>
          <div className="nav-cta">
            {/* Sign In goes to the FBR system login. PRA has its own login on its subdomain. */}
            <button className="signin-link" onClick={() => navigate('/login')}>Sign In</button>
            <Link className="btn btn-primary btn-sm" to="/#contact">Book a Free Demo</Link>
          </div>
        </div>
      </nav>

      {children}

      <footer>
        <div className="wrap">
          <div className="foot-grid">
            <div className="foot-brand">
              <Link to="/"><img src="/marketing/images/axiom_square.jpeg" alt="AXIOM SQUARE" /></Link>
              <p>Business Consultants (Private) Limited. FBR and PRA digital invoicing, income tax
                 filing, and Odoo integration for businesses across Pakistan.</p>
            </div>
            <div className="foot-col">
              <h3>Services</h3>
              {NAV.map((n) => <Link key={n.key} to={n.to}>{n.label}</Link>)}
            </div>
            <div className="foot-col">
              <h3>Sign In</h3>
              <button className="foot-link-btn" onClick={() => navigate('/login')}>FBR Digital Invoicing</button>
              <a href="https://pra.axiomsquarepk.com/login">PRA Digital Invoicing</a>
              <a href="/guides/">Blogs</a>
            </div>
            <div className="foot-col">
              <h3>Contact</h3>
              <a href="mailto:contact@axiomsquare.com">contact@axiomsquare.com</a>
              <a href="tel:+923219630757">+92 321 9630757</a>
              <Link to="/#contact">558 P Johar Town, Lahore</Link>
            </div>
            <div className="foot-col">
              <h3>Follow</h3>
              <div className="foot-social">
                <a href="https://pk.linkedin.com/company/axiom-square-business-consultants" target="_blank" rel="noopener noreferrer" aria-label="Axiom Square on LinkedIn">
                  <svg viewBox="0 0 24 24"><path d="M6.94 5a2 2 0 1 1 0 4 2 2 0 0 1 0-4zM4.7 9.9h4.5V20H4.7V9.9zm7 0h4.3v1.38h.06c.6-1.05 2.06-2.16 4.24-2.16 4.53 0 5.37 2.78 5.37 6.4V20h-4.5v-4.62c0-1.1-.02-2.52-1.62-2.52-1.63 0-1.88 1.2-1.88 2.44V20h-4.5V9.9z"/></svg>
                </a>
                <a href="https://www.facebook.com/profile.php?id=61582457094655" target="_blank" rel="noopener noreferrer" aria-label="Axiom Square on Facebook">
                  <svg viewBox="0 0 24 24"><path d="M13.5 21v-7.6h2.6l.4-3h-3v-1.9c0-.87.25-1.46 1.55-1.46h1.65V4.35C15.9 4.24 15.02 4.17 14 4.17c-2.14 0-3.6 1.28-3.6 3.63v2.6H7.8v3h2.6V21h3.1z"/></svg>
                </a>
                <a href="https://www.instagram.com/axiomsquare" target="_blank" rel="noopener noreferrer" aria-label="Axiom Square on Instagram">
                  <svg viewBox="0 0 24 24"><path d="M12 3c2.44 0 2.75.01 3.71.05.96.05 1.62.2 2.2.42.6.24 1.1.55 1.6 1.05.5.5.81 1 1.05 1.6.22.58.37 1.24.42 2.2.04.96.05 1.27.05 3.71s-.01 2.75-.05 3.71c-.05.96-.2 1.62-.42 2.2a4.3 4.3 0 0 1-1.05 1.6c-.5.5-1 .81-1.6 1.05-.58.22-1.24.37-2.2.42-.96.04-1.27.05-3.71.05s-2.75-.01-3.71-.05c-.96-.05-1.62-.2-2.2-.42a4.3 4.3 0 0 1-1.6-1.05 4.3 4.3 0 0 1-1.05-1.6c-.22-.58-.37-1.24-.42-2.2C3.01 14.75 3 14.44 3 12s.01-2.75.05-3.71c.05-.96.2-1.62.42-2.2.24-.6.55-1.1 1.05-1.6.5-.5 1-.81 1.6-1.05.58-.22 1.24-.37 2.2-.42C9.25 3.01 9.56 3 12 3zm0 5a4 4 0 1 0 0 8 4 4 0 0 0 0-8zm0 6.6A2.6 2.6 0 1 1 12 9.4a2.6 2.6 0 0 1 0 5.2zm4.9-6.8a.94.94 0 1 1-1.88 0 .94.94 0 0 1 1.88 0z"/></svg>
                </a>
                <a href="https://www.youtube.com/@axiomsquare" target="_blank" rel="noopener noreferrer" aria-label="Axiom Square on YouTube">
                  <svg viewBox="0 0 24 24"><path d="M21.6 7.7a2.7 2.7 0 0 0-1.9-1.9C18 5.3 12 5.3 12 5.3s-6 0-7.7.5A2.7 2.7 0 0 0 2.4 7.7C2 9.4 2 12 2 12s0 2.6.4 4.3a2.7 2.7 0 0 0 1.9 1.9c1.7.5 7.7.5 7.7.5s6 0 7.7-.5a2.7 2.7 0 0 0 1.9-1.9c.4-1.7.4-4.3.4-4.3s0-2.6-.4-4.3zM10 15.2V8.8l5.5 3.2-5.5 3.2z"/></svg>
                </a>
                <a href="https://www.google.com/search?kgmid=/g/11nvgwz1kl" target="_blank" rel="noopener noreferrer" aria-label="Axiom Square on Google">
                  <svg viewBox="0 0 24 24"><path d="M12 10.2v3.9h5.5c-.24 1.4-1.7 4.1-5.5 4.1-3.3 0-6-2.7-6-6.2s2.7-6.2 6-6.2c1.9 0 3.15.8 3.87 1.5l2.64-2.55C16.9 3.02 14.7 2 12 2 6.98 2 2.9 6.1 2.9 11s4.08 9 9.1 9c5.25 0 8.75-3.7 8.75-8.9 0-.6-.07-1.05-.15-1.5H12z"/></svg>
                </a>
              </div>
            </div>
          </div>
          <div className="foot-bottom">
            <span>&copy; 2026 AXIOM SQUARE Business Consultants (Private) Limited. All rights reserved.</span>
            <span>Lahore, Pakistan</span>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default MarketingLayout;
